import React, { useState, useEffect } from 'react';
import { 
  Instagram, 
  Facebook, 
  Building2, 
  RefreshCw, 
  Eye, 
  EyeOff,
  Search,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  User,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.glowytics.com';

const MetaInstagramDebug = () => {
  const [brandId, setBrandId] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Results
  const [tokenAccounts, setTokenAccounts] = useState(null);
  const [pageAccounts, setPageAccounts] = useState(null);
  const [adAccountAccounts, setAdAccountAccounts] = useState(null);
  
  // Loading states
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingAdAccount, setLoadingAdAccount] = useState(false);

  // Get current brandId from storage on mount
  useEffect(() => {
    const storedBrandId = localStorage.getItem('currentBrandId');
    console.log('[MetaInstagramDebug] Current brand ID from localStorage:', storedBrandId);
    if (storedBrandId) {
      setBrandId(storedBrandId);
      // Auto-load the brand connection
      fetchBrandMetaConnection(storedBrandId);
    }
  }, []);

  // eslint-disable-next-line no-unused-vars
  const fetchBrands = async () => {
    try {
      const storedBrandId = localStorage.getItem('currentBrandId');
      const response = await axios.get(`${API_BASE}/api/brands`, {
        withCredentials: true,
        headers: storedBrandId ? { 'X-Brand-ID': storedBrandId } : {}
      });
      setBrands(response.data.brands || response.data || []);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchBrandMetaConnection = async (targetBrandId) => {
    if (!targetBrandId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/meta-ads/connection/${targetBrandId}`, {
        withCredentials: true,
        headers: { 'X-Brand-ID': targetBrandId }
      });
      setSelectedBrand(response.data);
      // Clear previous results
      setTokenAccounts(null);
      setPageAccounts(null);
      setAdAccountAccounts(null);
    } catch (error) {
      console.error('Failed to fetch brand connection:', error);
      setSelectedBrand(null);
    } finally {
      setLoading(false);
    }
  };

  // 1. Get Instagram accounts from User/Token level
  const fetchTokenInstagramAccounts = async () => {
    if (!selectedBrand?.accessToken) return;
    setLoadingToken(true);
    setTokenAccounts(null);
    
    try {
      const response = await axios.get(`${API_BASE}/api/meta-ads/debug/token-instagram-accounts`, {
        params: { brandId: selectedBrand.brandId },
        withCredentials: true
      });
      setTokenAccounts(response.data);
    } catch (error) {
      setTokenAccounts({ error: error.response?.data?.error || error.message });
    } finally {
      setLoadingToken(false);
    }
  };

  // 2. Get Instagram accounts from Facebook Page
  const fetchPageInstagramAccounts = async () => {
    if (!selectedBrand?.pageId) return;
    setLoadingPage(true);
    setPageAccounts(null);
    
    try {
      const response = await axios.get(`${API_BASE}/api/meta-ads/debug/page-instagram-accounts`, {
        params: { brandId: selectedBrand.brandId },
        withCredentials: true
      });
      setPageAccounts(response.data);
    } catch (error) {
      setPageAccounts({ error: error.response?.data?.error || error.message });
    } finally {
      setLoadingPage(false);
    }
  };

  // 3. Get Instagram accounts from Ad Account
  const fetchAdAccountInstagramAccounts = async () => {
    if (!selectedBrand?.adAccountId) return;
    setLoadingAdAccount(true);
    setAdAccountAccounts(null);
    
    try {
      const response = await axios.get(`${API_BASE}/api/meta-ads/debug/adaccount-instagram-accounts`, {
        params: { brandId: selectedBrand.brandId },
        withCredentials: true
      });
      setAdAccountAccounts(response.data);
    } catch (error) {
      setAdAccountAccounts({ error: error.response?.data?.error || error.message });
    } finally {
      setLoadingAdAccount(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const maskToken = (token) => {
    if (!token) return '';
    if (token.length <= 20) return '••••••••••••••••';
    return token.substring(0, 10) + '••••••••••••••••••••' + token.substring(token.length - 10);
  };

  const ResultCard = ({ title, icon: Icon, data, loading, color }) => (
    <div className={`bg-gray-800/50 border border-gray-700 rounded-xl p-5`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-white font-semibold">{title}</h3>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : data === null ? (
        <div className="text-gray-500 text-sm py-4 text-center">
          Henüz sorgulanmadı
        </div>
      ) : data.error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium text-sm">Hata</p>
              <p className="text-red-300/70 text-xs mt-1">{data.error}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.accounts?.length === 0 ? (
            <div className="text-gray-500 text-sm py-4 text-center">
              Instagram hesabı bulunamadı
            </div>
          ) : (
            data.accounts?.map((account, idx) => (
              <div 
                key={account.id || idx}
                className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {account.profile_picture_url ? (
                      <img 
                        src={account.profile_picture_url} 
                        alt={account.username}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">
                        @{account.username || 'Unknown'}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {account.name || account.ig_id || 'Instagram Account'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-800 px-2 py-1 rounded text-purple-400 font-mono">
                      {account.id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(account.id, `${title}-${idx}`)}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    >
                      {copiedId === `${title}-${idx}` ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                {account.business_id && (
                  <div className="mt-2 pt-2 border-t border-gray-700/50">
                    <p className="text-xs text-gray-500">
                      Business ID: <code className="text-blue-400">{account.business_id}</code>
                    </p>
                  </div>
                )}
                {account.ig_id && (
                  <div className="mt-2 pt-2 border-t border-gray-700/50">
                    <p className="text-xs text-gray-500">
                      IG User ID: <code className="text-green-400">{account.ig_id}</code>
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
          {data.raw && (
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                Raw Response
              </summary>
              <pre className="mt-2 text-xs bg-gray-900 p-3 rounded overflow-auto max-h-40 text-gray-400">
                {JSON.stringify(data.raw, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
              <Instagram className="w-6 h-6 text-white" />
            </div>
            Meta Instagram Debug Tool
          </h1>
          <p className="text-gray-400 mt-2">
            Instagram hesap erişimlerini farklı kaynaklardan test et
          </p>
        </div>

        {/* Brand Selection */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Marka {selectedBrand?.brandName && <span className="text-purple-400">({selectedBrand.brandName})</span>}
          </label>
          <div className="flex gap-3">
            {brands.length > 0 ? (
              <select
                value={brandId}
                onChange={(e) => {
                  setBrandId(e.target.value);
                  if (e.target.value) fetchBrandMetaConnection(e.target.value);
                }}
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Marka Seç --</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                placeholder="Brand ID girin..."
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              />
            )}
            <button
              onClick={() => brandId && fetchBrandMetaConnection(brandId)}
              disabled={!brandId || loading}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {loading ? 'Yükleniyor...' : 'Yenile'}
            </button>
          </div>
          {brandId && !selectedBrand && !loading && (
            <p className="text-yellow-500 text-xs mt-2">
              ⚠️ Bu marka için Meta bağlantısı bulunamadı
            </p>
          )}
        </div>

        {/* Connection Info */}
        {selectedBrand && (
          <>
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 mb-6">
              <h2 className="text-white font-semibold mb-4">Bağlantı Bilgileri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Ad Account ID</p>
                  <p className="text-white font-mono text-sm">{selectedBrand.adAccountId || '-'}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Facebook Page ID</p>
                  <p className="text-white font-mono text-sm">{selectedBrand.pageId || '-'}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">DB Instagram ID</p>
                  <p className="text-white font-mono text-sm">{selectedBrand.instagramId || '-'}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">DB Instagram Name</p>
                  <p className="text-white font-mono text-sm">{selectedBrand.instagramName || '-'}</p>
                </div>
              </div>
              
              {/* Token (Hidden) */}
              <div className="mt-4 bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">Access Token</p>
                  <button
                    onClick={() => setShowToken(!showToken)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-white font-mono text-xs break-all">
                  {showToken ? selectedBrand.accessToken : maskToken(selectedBrand.accessToken)}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={fetchTokenInstagramAccounts}
                disabled={loadingToken}
                className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-700 disabled:to-gray-700 rounded-xl text-white font-medium flex items-center justify-center gap-3 transition-all"
              >
                {loadingToken ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
                Token Instagram
              </button>
              
              <button
                onClick={fetchPageInstagramAccounts}
                disabled={loadingPage || !selectedBrand.pageId}
                className="p-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 disabled:from-gray-700 disabled:to-gray-700 rounded-xl text-white font-medium flex items-center justify-center gap-3 transition-all"
              >
                {loadingPage ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Facebook className="w-5 h-5" />
                )}
                Page Instagram
              </button>
              
              <button
                onClick={fetchAdAccountInstagramAccounts}
                disabled={loadingAdAccount || !selectedBrand.adAccountId}
                className="p-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-700 disabled:to-gray-700 rounded-xl text-white font-medium flex items-center justify-center gap-3 transition-all"
              >
                {loadingAdAccount ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Building2 className="w-5 h-5" />
                )}
                Ad Account Instagram
              </button>
            </div>

            {/* Results */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ResultCard
                title="Token Level"
                icon={RefreshCw}
                data={tokenAccounts}
                loading={loadingToken}
                color="bg-blue-600"
              />
              <ResultCard
                title="Facebook Page"
                icon={Facebook}
                data={pageAccounts}
                loading={loadingPage}
                color="bg-indigo-600"
              />
              <ResultCard
                title="Ad Account"
                icon={Building2}
                data={adAccountAccounts}
                loading={loadingAdAccount}
                color="bg-purple-600"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MetaInstagramDebug;

